import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Step, CallBackProps, STATUS, TooltipRenderProps, EVENTS, ACTIONS } from "react-joyride";
import { TUTORIALS, TutorialStep } from "../tutorial";
import { useRecoilState } from "recoil";
import { projectCountInfoState, projectImagesInfoState } from "../recoil";
import { getProject } from "../services/projectService";
import { ImageInfoItem } from "../components/new/ImageTable";
import { ILabelCount } from "@types";

function toJoyrideStep(step: TutorialStep): Step {
  return {
    target: step.target,
    content: step.content,
    spotlightClicks: step.spotlightClicks ?? false,
    hideFooter: step.hideFooter ?? false,
    disableOverlayClose: step.disableOverlayClose ?? false,
    placement: step.placement ?? "bottom",
    disableBeacon: true,
  };
}

// Context 타입 정의
export interface TutorialContextType {
  run: boolean;
  tutorialKey: TutorialKey;
  stepIndex: number;
  currentStep: Step; // toJoyrideStep 반환 타입
  startTour: (key: TutorialKey, startIndex?: number) => void;
  nextSubStep: () => void;
  handleJoyrideCallback: (data: CallBackProps) => void;
  setRun: (run: boolean) => void;
  toastVisible: boolean;
  startTourWithDelay: (key: TutorialKey, navigate: (path: string) => void, targetPath: string, delay?: number) => void;
  pendingStartRef: React.MutableRefObject<boolean>;
  handleValidationTutorialNext: () => Promise<void>;
  stepIndexRef: React.MutableRefObject<number>;
  tutorialKeyRef: React.MutableRefObject<TutorialKey>;
}

export const TutorialContext = createContext<TutorialContextType>({} as TutorialContextType);

export type TutorialKey =
  | "cls-project"
  | "inspection-project"
  | "analysis-project"
  | "inspection-status"
  | "report-generation";

export const TutorialProvider = ({ children }: { children: React.ReactNode }) => {
  const [run, setRun] = useState(false);
  const [tutorialKey, setTutorialKey] = useState<TutorialKey>("cls-project");
  const [stepIndex, setStepIndex] = useState(0);
  const [toastVisible, setToastVisible] = useState(false);
  const [projectImagesInfo, setProjectImagesInfo] = useRecoilState(projectImagesInfoState);
  const [projectCountInfo, setProjectCountInfo] = useRecoilState(projectCountInfoState);

  const pendingStartRef = useRef(false);
  const stepIndexRef = useRef(0);
  const tutorialKeyRef = useRef<TutorialKey>("cls-project");

  useEffect(() => {
    stepIndexRef.current = stepIndex;
  }, [stepIndex]);

  const currentSteps = TUTORIALS[tutorialKey];

  const currentTutorialStep = currentSteps[stepIndex] ?? currentSteps[0];
  const currentStep = toJoyrideStep(currentTutorialStep);

  const startTour = (key: TutorialKey, startIndex = 0) => {
    setTutorialKey(key);
    tutorialKeyRef.current = key;
    setStepIndex(startIndex);
    stepIndexRef.current = startIndex;
    setRun(true);
  };

  // 개발진행시 tutorial on 모드로 진행
  // useEffect(() => {
  //   startTour();
  // }, []);

  const nextSubStep = useCallback(() => {
    const si = stepIndexRef.current;
    const steps = TUTORIALS[tutorialKeyRef.current];

    const nextSi = si + 1;
    if (nextSi < steps.length) {
      stepIndexRef.current = nextSi;
      setStepIndex(nextSi);
    } else {
      setRun(false);
      stepIndexRef.current = 0;
      setStepIndex(0);
    }
  }, []);

  useEffect(() => {
    if (!run) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as Element;
      const field = target.closest("input, textarea") as HTMLInputElement | null;
      if (field && field.type !== "radio" && field.type !== "checkbox") return;

      const currentSubStep = TUTORIALS[tutorialKeyRef.current][stepIndexRef.current];

      if (currentSubStep?.nextTrigger) return;
      if (!currentSubStep?.spotlightClicks) return;

      if (target.closest(currentStep.target as string)) {
        setTimeout(() => nextSubStep(), 100);
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [run, currentStep.target, nextSubStep]);

  useEffect(() => {
    if (!run) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Enter") return;

      const active = document.activeElement;
      if (active?.tagName === "INPUT" || active?.tagName === "TEXTAREA") return;

      const currentSubStep = TUTORIALS[tutorialKeyRef.current][stepIndexRef.current];

      if (currentSubStep?.nextTrigger) return;
      if (currentSubStep?.spotlightClicks) return;

      e.preventDefault();
      nextSubStep();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [run, nextSubStep]);

  const startTourWithDelay = (key: TutorialKey, navigate: (path: string) => void, targetPath: string, delay = 2000) => {
    setToastVisible(true);
    setTimeout(() => {
      setToastVisible(false);
      pendingStartRef.current = true;
      tutorialKeyRef.current = key;
      setTutorialKey(key);
      navigate(targetPath);
    }, delay);
  };

  const endTour = () => {
    setRun(false);
    setStepIndex(0);
    stepIndexRef.current = 0;
  };

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, type, action } = data;
    if (action === ACTIONS.CLOSE || [STATUS.FINISHED, STATUS.SKIPPED].includes(status as any)) {
      endTour();
      return;
    }
    if (type === EVENTS.STEP_AFTER && action === ACTIONS.NEXT) {
      const steps = TUTORIALS[tutorialKey];
      const currentSubStep = steps[stepIndex];
      if (currentSubStep?.nextTrigger) return;
      nextSubStep();
    }
  };

  const handleValidationTutorialNext = async () => {
    const data = await getProject(316);
    const imagesData = data.project.images as ImageInfoItem[];
    const sorted = [...imagesData].sort((a, b) => {
      const getGroup = (item: ImageInfoItem) => {
        if (!item.label) return 2;
        if (item.label === item.AILabel) return 0;
        return 1;
      };

      const ga = getGroup(a);
      const gb = getGroup(b);

      if (ga !== gb) return ga - gb;

      if (ga === 0) return a.score - b.score;
      return b.score - a.score;
    });
    setProjectImagesInfo(sorted);
    setProjectCountInfo({
      total_count: sorted.length,
      label_count: sorted.reduce((acc, img) => {
        if (img.label) {
          acc[img.label] = (acc[img.label] || 0) + 1;
        }
        acc.total = (acc.total || 0) + (img.label ? 1 : 0);
        return acc;
      }, {} as ILabelCount),
    });
    nextSubStep();
  };

  return (
    <TutorialContext.Provider
      value={{
        run,
        tutorialKey,
        tutorialKeyRef,
        stepIndex,
        currentStep,
        startTour,
        nextSubStep,
        handleJoyrideCallback,
        setRun,
        toastVisible,
        startTourWithDelay,
        pendingStartRef,
        handleValidationTutorialNext,
        stepIndexRef,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = () => {
  const ctx = useContext(TutorialContext);
  if (!ctx) throw new Error("TutorialProvider 안에서 사용해야 해요");
  return ctx;
};
